import { birthdateToDate } from './index.jsx';

/**
 * SKIN-374 — the client half of a personal-data edit.
 *
 * Personal fields are re-encrypted on every save with a fresh nonce, so the
 * server cannot tell a re-saved value from a changed one (SKIN-373). Only the
 * client holds the plaintext, so it compares before encrypting and declares
 * the fields it changed.
 */

export interface PersonalFieldDef {
    name: string;
    type: string;
    encrypted?: boolean;
}

/**
 * The plaintext a field is stored as: a Date becomes its epoch-ms string, and a
 * date field is pinned to UTC midnight so it names the same calendar day
 * everywhere. The encrypt loop and the change check both go through this, so
 * "changed" means exactly "would be stored differently".
 */
export function toStoredPlaintext(field: PersonalFieldDef, value: unknown): string {
    let plain: unknown = value;
    if (plain instanceof Date) plain = plain.getTime().toString();
    if (field.type === 'date' && (typeof plain === 'number' || (typeof plain === 'string' && /^\d+$/.test(plain)))) {
        const d = new Date(Number(plain));
        plain = String(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    return plain === null || plain === undefined ? '' : String(plain);
}

/**
 * A date field compares by calendar day, not by text: stored birthdates come as
 * YYYY-MM-DD (imported) or epoch-ms (natively created), so the same day can be
 * written two ways. The new value is compared as it would be stored.
 */
function sameDay(field: PersonalFieldDef, stored: unknown, edited: unknown): boolean {
    const a = birthdateToDate(stored);
    const b = birthdateToDate(toStoredPlaintext(field, edited));
    if (!a || !b) return !a && !b;
    return a.getTime() === b.getTime();
}

/** Names of the fields whose plaintext the form changed, in form order. */
export function changedPersonalFields(
    fields: PersonalFieldDef[],
    before: Record<string, unknown>,
    after: Record<string, unknown>,
): string[] {
    return fields
        .filter((field) => field.name in after)
        .filter((field) => (field.type === 'date'
            ? !sameDay(field, before[field.name], after[field.name])
            : toStoredPlaintext(field, before[field.name]) !== toStoredPlaintext(field, after[field.name])))
        .map((field) => field.name);
}

const EDIT = 'hospital.patient.personal-data-edit';

/** sherwood-api PersonalDataEditErrorCode (SKIN-373). */
const API_REFUSAL = { REASON_REQUIRED: 1, CHANGED_FIELDS_INVALID: 2 } as const;

/** The message for an edit the API refused, else a generic failure. */
export function personalDataEditErrorKey(error: any): string {
    const code = error?.response?.data?.errorCode ?? error?.errorCode;
    if (code === API_REFUSAL.REASON_REQUIRED) return `${EDIT}.reason-required`;
    if (code === API_REFUSAL.CHANGED_FIELDS_INVALID) return `${EDIT}.changed-fields-invalid`;
    return `${EDIT}.failed`;
}
